import {
  type Credentials,
  loadCredentials,
  saveCredentials,
  clearCredentials,
} from './credentials';

/**
 * The HTTP layer. Knows about tokens and error shapes; knows nothing about the
 * outbox.
 */

/** A response the server explained. `messageLo` is safe to show the shopkeeper. */
export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    readonly messageLo: string,
  ) {
    super(`${status} ${code}`);
    this.name = 'ApiError';
  }
}

/**
 * The request never reached the server, or its answer never came back.
 *
 * Kept distinct from `ApiError` because the two mean opposite things for an
 * outbox event: a transport failure is a retry, a server refusal may not be.
 * Collapsing them is how an offline moment turns into a `conflict` and a bill
 * disappears with no explanation.
 */
export class NetworkError extends Error {
  constructor(cause: unknown) {
    super('Could not reach the server');
    this.name = 'NetworkError';
    this.cause = cause;
  }
}

/** Refresh this long before the access token actually expires. */
const REFRESH_MARGIN_MS = 60_000;

/** A slow server is better than a hung till: the sale is already recorded. */
const TIMEOUT_MS = 20_000;

async function parse(response: Response): Promise<unknown> {
  const text = await response.text();
  if (!text) return undefined;
  try {
    return JSON.parse(text) as unknown;
  } catch {
    // nginx answers 413 and 502 with HTML. Parsing it as JSON and reporting
    // whatever fell out would be worse than admitting we cannot read it.
    return undefined;
  }
}

function errorFrom(status: number, body: unknown): ApiError {
  const error = (body as { error?: { code?: string; messageLo?: string } } | undefined)?.error;
  return new ApiError(
    status,
    error?.code ?? 'error',
    error?.messageLo ?? 'ຕໍ່ອິນເຕີເນັດບໍ່ໄດ້ ລອງໃໝ່ພາຍຫຼັງ',
  );
}

async function send(
  baseUrl: string,
  path: string,
  init: RequestInit & { token?: string; allow?: number[] },
): Promise<unknown> {
  const { token, allow = [], ...rest } = init;
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), TIMEOUT_MS);
  let response: Response;
  try {
    response = await fetch(`${baseUrl}${path}`, {
      ...rest,
      signal: controller.signal,
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        ...rest.headers,
      },
    });
  } catch (cause) {
    throw new NetworkError(cause);
  } finally {
    clearTimeout(timer);
  }

  const body = await parse(response);
  // `allow` is for statuses that carry a real answer rather than a failure —
  // 300, where the credentials were right and the server is asking which shop.
  // `fetch` reports those as `ok === false`, so without this the answer is
  // thrown away and reported as an error.
  if (!response.ok && !allow.includes(response.status)) {
    throw errorFrom(response.status, body);
  }
  return body;
}

/**
 * Logs a shop person in — the owner opening the app in a browser rather than a
 * till being enrolled.
 *
 * Returns the shops to choose from when the person owns more than one. Guessing
 * would silently show them another shop's takings, so the server refuses to.
 */
export async function login(
  baseUrl: string,
  login: string,
  password: string,
  shopCode?: string,
): Promise<Credentials | { shops: { code: string; name: string }[] }> {
  const body = await send(baseUrl, '/api/v1/auth/token', {
    method: 'POST',
    body: JSON.stringify({ login, password, ...(shopCode ? { shop: shopCode } : {}) }),
    // 300 Multiple Choices is the "which shop?" answer, not a failure.
    allow: [300],
  });

  const answer = body as {
    needsShop?: boolean;
    shops?: { code: string; name: string }[];
    accessToken?: string;
    refreshToken?: string;
    expiresIn?: number;
    shop?: { id: string; name: string };
    user?: { id: string };
  };

  if (answer.needsShop && answer.shops) return { shops: answer.shops };
  if (!answer.accessToken || !answer.refreshToken || !answer.shop || !answer.user) {
    throw new ApiError(500, 'bad_response', 'ຂໍ້ມູນທີ່ໄດ້ຮັບບໍ່ຄົບ ລອງໃໝ່');
  }

  const credentials: Credentials = {
    baseUrl,
    accessToken: answer.accessToken,
    accessExpiresAt: Date.now() + (answer.expiresIn ?? 900) * 1000,
    refreshToken: answer.refreshToken,
    shopId: answer.shop.id,
    shopName: answer.shop.name,
    login,
  };
  await saveCredentials(credentials);
  return credentials;
}

export interface CredentialChange {
  currentPassword: string;
  /** Omit to leave it alone. */
  login?: string;
  password?: string;
}

/**
 * Changes the signed-in person's own login and/or password.
 *
 * The server ends every other session when either changes and answers with a
 * fresh pair for this one, which is written here before the call returns — so
 * the till doing the change keeps working and the phone left on someone's
 * counter does not. Skipping that save would sign this device out instead,
 * which is exactly backwards.
 */
export async function changeCredentials(change: CredentialChange): Promise<{ login: string }> {
  const stored = await loadCredentials();
  if (!stored) throw new ApiError(401, 'signed_out', 'ຍັງບໍ່ໄດ້ເຂົ້າສູ່ລະບົບ');

  const body = (await authed('/api/v1/me/credentials', {
    method: 'PUT',
    body: JSON.stringify(change),
  })) as {
    accessToken: string;
    refreshToken: string;
    expiresIn: number;
    user: { login: string };
  };

  await saveCredentials({
    ...stored,
    accessToken: body.accessToken,
    accessExpiresAt: Date.now() + body.expiresIn * 1000,
    refreshToken: body.refreshToken,
    login: body.user.login,
  });
  return { login: body.user.login };
}

/**
 * A live access token, refreshing first if it is about to expire.
 *
 * The new credential is written to disk **before** it is returned. A till that
 * dies in between would otherwise come back holding a token the server has
 * already retired, and retry with one it never saved.
 */
async function freshToken(credentials: Credentials): Promise<Credentials> {
  if (credentials.accessExpiresAt - Date.now() > REFRESH_MARGIN_MS) return credentials;

  const body = (await send(credentials.baseUrl, '/api/v1/auth/refresh', {
    method: 'POST',
    body: JSON.stringify({ refreshToken: credentials.refreshToken }),
  })) as { accessToken: string; refreshToken: string; expiresIn: number };

  const next: Credentials = {
    ...credentials,
    accessToken: body.accessToken,
    accessExpiresAt: Date.now() + body.expiresIn * 1000,
    refreshToken: body.refreshToken,
  };
  await saveCredentials(next);
  return next;
}

/**
 * An authenticated request, refreshing as needed.
 *
 * A 401 that survives a refresh means the device was un-enrolled or its token
 * family was revoked. The credential is cleared so the app can ask to be
 * enrolled again — but nothing in the outbox is ever dropped, because those
 * events are the only record that the sales happened.
 */
export async function authed(path: string, init: RequestInit = {}): Promise<unknown> {
  const stored = await loadCredentials();
  if (!stored) throw new ApiError(401, 'signed_out', 'ຍັງບໍ່ໄດ້ເຂົ້າສູ່ລະບົບ');

  let credentials = await freshToken(stored);
  try {
    return await send(credentials.baseUrl, path, { ...init, token: credentials.accessToken });
  } catch (error) {
    if (!(error instanceof ApiError) || error.status !== 401) throw error;

    // Force one rotation, in case the token expired between the check and the
    // request — a real window on a slow connection.
    credentials = await freshToken({ ...credentials, accessExpiresAt: 0 });
    try {
      return await send(credentials.baseUrl, path, { ...init, token: credentials.accessToken });
    } catch (retry) {
      if (retry instanceof ApiError && retry.status === 401) await clearCredentials();
      throw retry;
    }
  }
}
